import type { StationBrand, CalendarEvent } from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { useCalendarStore, useStationStore } from '../index';
import { useMenuStore } from '@centric-os/stores/menus/use-menu-store';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { storeToRefs } from 'pinia';

interface State extends StateTree {
  selectedStation: StationBrand;
  showMenuScheduleForm: boolean;
  showStationClosureForm: boolean;
  eventFormEdited: boolean;
  allowStationCompare: boolean;
  stationsToCompare: StationBrand[];
  currentSchedulePanelTabIndex: number;
}

export function useStationScheduleStore(storeId = 'stationSchedule') {
  const store = defineStore(storeId, {
    state: (): State => ({
      selectedStation: null,
      showMenuScheduleForm: false,
      showStationClosureForm: false,
      eventFormEdited: false,
      allowStationCompare: false,
      stationsToCompare: [],
      currentSchedulePanelTabIndex: 0,
    }),
    actions: {
      async deleteMenuSchedule(menuSchedule?: CalendarEvent): Promise<void> {
        const calendarStore = useCalendarStore();
        const { structuredStationCalendarEvents, currentEvent } = storeToRefs(calendarStore);
        const stationStore = useStationStore();
        const { stationBrand } = storeToRefs(stationStore);
        const menuStore = useMenuStore();
        const siteStore = useSiteStore();
        const { updateSiteOnNextMount } = storeToRefs(siteStore);

        const scheduleToDelete: CalendarEvent = menuSchedule ?? currentEvent.value;

        if (!scheduleToDelete) {
          console.error('No schedule to delete');
          return;
        }
        // Check if stationBrand exists and has an id
        if (!stationBrand.value?.id) {
          console.error('Station brand is not available or missing ID');
          return;
        }

        /*
          V4 MENU:
          - If the menu is a v4 menu, we need to unschedule the menu from the station.
        */
        if (scheduleToDelete.data?.version >= 4) {
          try {
            await menuStore.unscheduleMenu({
              menu_id: scheduleToDelete.data.menu,
              station_id: stationBrand.value.id,
            });
          } catch (error) {
            console.error('Failed unscheduling menu:', error);
          }
        }

        // Delete the calendar event
        await calendarStore.deleteCalendarEvent(stationBrand.value.id, scheduleToDelete);

        // Refresh the calendar data
        const activeCalendarEventIds = Object.keys(structuredStationCalendarEvents.value);
        await calendarStore.getAllLocationCalendarEvents(activeCalendarEventIds, true);

        // Update store state
        updateSiteOnNextMount.value = true;
        this.showMenuScheduleForm = false;
        this.eventFormEdited = false;
      },
    },
    getters: {},
    pagination: false,
  });
  return store();
}
